/* eslint-disable no-unused-vars */
import _ from 'lodash';
import moment from 'moment';

import { removeUnavailableTreeData } from '@components/tree-component';
import { searchEquipmentData } from '@utilities/equipment-search';

import { getDataPoints } from '@pages/settings/helper';
import { cleanString } from '@utilities/attachments';
import {
  createAtOrSyncedDateFilter,
  dueDateFilter,
  scheduledDateFilter
} from '@utilities/date-filters';
import { sortFilterTags } from '@utilities/filter-is-changed';

export const getFilterEquipment = ({
  filters,
  sitesMap,
  equipments,
  SelectedFilter,
  jurisdictionNumbersMap,
  picklistsMap,
  equipmentDisplayFields,
  cacheSites,
  statusPriority = false,
  store
}) => {
  const {
    area,
    proximity,
    siteType,
    siteStatus,
    equipmentCategory,
    equipmentType,
    equipmentSubType,
    equipmentStatus,
    searched = '',
    taskType,
    taskStatus,
    priority,
    workOrderPriority,
    assign,
    scheduledDate,
    download,
    dueDate: filterDueDate,
    recurringTemplate,
    workOrderType = [],
    createdAt,
    syncedDate: syncedDateFilter,
    checkRecurrancePriority,
    inspectionType,
    inspectionRun,
    checkType
  } = filters;

  const sitesSiteTypeData = {},
    sitesSiteStatusData = {},
    equipmentsEquipmentTypeData = {},
    equipmentsEquipmentStatusData = {},
    equipmentsEquipmentSubTypeData = {},
    equipmentsEquipmentCategoryData = {},
    sitesAreaHierarchyTreeData = {},
    sitesWorkOrderPriorityDataMap = {};

  const inspectionTypeMap = inspectionType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const inspectionRunMap = inspectionRun?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedRecurrancePriority = checkRecurrancePriority?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedWorkOrderRecurringTemplateMap = recurringTemplate?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedAreaMap = area?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const workOrderTypeMap = workOrderType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedSiteTypeMap = siteType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedSiteStatusMap = siteStatus?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedEquipmentTypeMap = equipmentType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedEquipmentStatusMap = equipmentStatus.value.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedEquipmentSubTypeMap = equipmentSubType.value.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedEquipmentCategoryMap = equipmentCategory.value.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const checkTypeMap = checkType?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedTaskType = taskType?.value?.includes('All')
    ? {
        'Work Order': true,
        'Work Request': true,
        Inspection: true,
        Check: true,
        'New or edited asset': true
      }
    : taskType.value.reduce((acc, curr) => {
        acc[
          curr === 'Work orders'
            ? 'Work Order'
            : curr === 'Work requests'
            ? 'Work Request'
            : curr === 'Inspections'
            ? 'Inspection'
            : curr === 'Check'
            ? 'Check'
            : curr === 'New or edited asset'
            ? 'New or edited asset'
            : 'Work Order'
        ] = true;
        return acc;
      }, {});

  const selectedTaskStatus = taskStatus?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const selectedWorkOrderPriority = workOrderPriority?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});
  const selectedPriority = priority?.value?.reduce((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {});

  const equipmentsWithDataPoints = equipments.map((props) => {
    const { jurisdictionNumber } = jurisdictionNumbersMap[props.equipmentId] || {};

    const dataPoints = getDataPoints(
      { ...props, subtype: props.equipmentSubType, jurisdictionNumber },
      equipmentDisplayFields,
      'equipment',
      picklistsMap
    );
    return {
      ...props,
      dataPoints
    };
  });

  let filteredEquipment = equipmentsWithDataPoints.filter((equipment) => {
    const {
      siteId,
      equipmentType: type,
      equipmentCategory: category,
      status,
      equipmentSubType: subType,
      allTasksForSite,
      alNewWorkOrderListSyncedAndInprogress = [],
      equipmentSyncedAndEditedTasks = []
    } = equipment;

    const site = cacheSites[sitesMap[siteId]] || {};

    if (!searchEquipmentData({ equipment, inputValue: searched, store })) return false;

    sitesSiteTypeData[site?.siteType] = true;
    sitesSiteStatusData[site?.status] = true;
    sitesAreaHierarchyTreeData[site?.areaHierarchy] = true;

    equipmentsEquipmentTypeData[type] = true;
    equipmentsEquipmentStatusData[status] = true;
    equipmentsEquipmentSubTypeData[subType] = true;
    equipmentsEquipmentCategoryData[category] = true;

    if (area.value.length > 0 && !selectedAreaMap[site?.areaHierarchy]) return false;
    if (siteType.value.length > 0 && !selectedSiteTypeMap[site?.siteType]) return false;
    if (siteStatus.value.length > 0 && !selectedSiteStatusMap[site?.status]) return false;
    if (equipmentType.value.length > 0 && !selectedEquipmentTypeMap[type]) return false;
    if (equipmentStatus.value.length > 0 && !selectedEquipmentStatusMap[status]) return false;
    if (equipmentSubType.value.length > 0 && !selectedEquipmentSubTypeMap[subType]) return false;
    if (equipmentCategory.value.length > 0 && !selectedEquipmentCategoryMap[category]) return false;
    if (proximity.value) {
      if (!site?.distance) return false;
      if (site.distance >= proximity.value) return false;
    }
    const dueCon = _.isArray(filterDueDate.value) ? !filterDueDate.value.includes('') : true;
    const scheduleCon = _.isArray(scheduledDate.value) ? !scheduledDate.value.includes('') : true;
    const createAtCon = _.isArray(createdAt?.value) ? !createdAt?.value.includes('') : true;
    const syncedDateCon = _.isArray(syncedDateFilter?.value)
      ? !syncedDateFilter?.value.includes('')
      : true;

    if (
      recurringTemplate.value.length > 0 &&
      !allTasksForSite.some(
        (x) => selectedWorkOrderRecurringTemplateMap[`${x.rtNumber} - ${x.rtName}`]
      )
    )
      return false;

    if (
      workOrderType?.value?.length > 0 &&
      ![...allTasksForSite, ...alNewWorkOrderListSyncedAndInprogress].some(
        (x) => workOrderTypeMap[x?.typeId]
      )
    )
      return false;

    if (
      taskType.value.length > 0 &&
      !(
        allTasksForSite.some(
          (x) => selectedTaskType[x?.newWorkRequest ? 'Work Request' : x.type]
        ) || equipmentSyncedAndEditedTasks?.some((x) => selectedTaskType[x.type])
      )
    )
      return false;

    if (
      taskStatus.value.length > 0 &&
      !(
        allTasksForSite.some((x) => selectedTaskStatus[x.status]) ||
        equipmentSyncedAndEditedTasks?.some((x) => selectedTaskStatus[x.status])
      )
    )
      return false;

    allTasksForSite?.map((x) => {
      if (!sitesWorkOrderPriorityDataMap[x.priority])
        sitesWorkOrderPriorityDataMap[x.priority] = true;
    });

    if (
      allTasksForSite?.some((x) => x.type === 'Checks') &&
      priority.value.length > 0 &&
      !allTasksForSite.some((x) => selectedPriority[x.priority])
    )
      return false;

    if (
      workOrderPriority.value.length > 0 &&
      !allTasksForSite.some((x) => x.type === 'Work Order' && selectedWorkOrderPriority[x.priority])
    )
      return false;

    if (
      checkRecurrancePriority?.value?.length > 0 &&
      !allTasksForSite.some(
        (x) => x.type === 'Check' && selectedRecurrancePriority[x.recurrencePriorityId]
      )
    )
      return false;

    if (
      inspectionType?.value?.length > 0 &&
      !allTasksForSite.some((x) => inspectionTypeMap[x.inspectionFormId])
    )
      return false;

    if (
      inspectionRun?.value.length > 0 &&
      !allTasksForSite.some((x) => inspectionRunMap[`${x.runNumber} - ${x.RunName}`?.trim()])
    )
      return false;

    if (
      checkType?.value.length > 0 &&
      !allTasksForSite.some((x) => checkTypeMap[x.inspectionTypeId])
    )
      return false;

    if (
      assign?.value?.length > 0 &&
      !allTasksForSite.some((y) => assign.value.some((r) => y.customAssignedTaskValue.includes(r)))
    )
      return false;

    if (
      !_.isEmpty(filterDueDate.value) &&
      dueCon &&
      !allTasksForSite.some((x) =>
        dueDateFilter({
          taskDueDate: x.dueDate ? x?.dueDate : x?.due ? x?.due : x?.nextDue,
          dueDateFilter: filterDueDate
        })
      )
    )
      return false;

    if (
      !_.isEmpty(scheduledDate.value) &&
      scheduleCon &&
      !allTasksForSite.some((x) =>
        scheduledDateFilter({
          startDate: x.startDate || x?.NextScheduled,
          syncedDate: x.syncedDate,
          dueDateFilter: scheduledDate
        })
      )
    )
      return false;

    if (
      !_.isEmpty(createdAt?.value) &&
      createAtCon &&
      !allTasksForSite.some((x) =>
        createAtOrSyncedDateFilter({
          dateForFilter: x?.createdDate,
          filterSelectedDate: createdAt
        })
      )
    )
      return false;

    if (
      !_.isEmpty(syncedDateFilter?.value) &&
      syncedDateCon &&
      !allTasksForSite.some((x) =>
        createAtOrSyncedDateFilter({
          dateForFilter: x.syncedDate,
          filterSelectedDate: syncedDateFilter
        })
      )
    )
      return false;

    return true;
  });

  filteredEquipment = statusPriority
    ? filteredEquipment
    : filteredEquipment?.sort((a, b) => {
        switch (SelectedFilter[0]) {
          case 'DUE':
            return dueDateSort(a, b);

          case 'DISTANCE':
            return distanceFilter(a, b, sitesMap, cacheSites);
          case 'A-Z':
            return a.name.localeCompare(b.name);

          case '# TASKS':
            return b?.allotDoneTasks?.length - a?.allotDoneTasks?.length;

          default:
            return a?.name?.localeCompare(b.name);
        }
      });

  filteredEquipment = filteredEquipment?.map((a) => {
    const dataPoints = a?.dataPointsForSearch || [];
    const cleanInput = a?.dataPointsForSearch?.length > 0 ? cleanString(searched) : '';

    let newDataPointsList = dataPoints.filter((item) => {
      let titleAndValueCombineObject = `${item.title} : ${item.value}`;
      return cleanString(String(titleAndValueCombineObject)).includes(cleanInput);
    });

    const siteDataPoints = a?.siteDataPointsForSearch || [];
    const cleanInputForSiteDataPoints =
      a?.siteDataPointsForSearch?.length > 0 ? cleanString(searched) : '';

    let newDataPointsForSiteList = siteDataPoints.filter((item) => {
      let titleAndValueCombineObject = `${item.title} : ${item.value}`;
      return cleanString(String(titleAndValueCombineObject)).includes(cleanInputForSiteDataPoints);
    });

    return {
      ...a,
      dataPoints: searched ? [...a.dataPoints, ...newDataPointsList] : a?.dataPoints,
      siteDataPoints:
        a?.dataPointsForSite?.length > 0 || a?.siteDataPointsForSearch?.length > 0
          ? searched
            ? [...a.dataPointsForSite, ...newDataPointsForSiteList]
            : a?.dataPointsForSite
          : []
    };
  });

  let sorted = [];
  if (statusPriority) {
    const priorityOrder = {
      'Pending sync': 1,
      'In progress': 2,
      // Synced: 3,
      'To do': 4
    };

    sorted = [...filteredEquipment].sort((a, b) => {
      const orderA = priorityOrder[a?.equipmentEditingStatus || 'To do'] || 1199;
      const orderB = priorityOrder[b?.equipmentEditingStatus || 'To do'] || 1199;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      switch (SelectedFilter[0]) {
        case 'DUE':
          return dueDateSort(a, b);

        case 'DISTANCE':
          return distanceFilter(a, b, sitesMap, cacheSites);
        case 'A-Z':
          return a.name.localeCompare(b.name);

        case '# TASKS':
          return b?.allotDoneTasks?.length - a?.allotDoneTasks?.length;

        default:
          return a?.name?.localeCompare(b.name);
      }
    });
  }

  return {
    filteredEquipment: statusPriority ? sorted : filteredEquipment,
    sitesSiteTypeData,
    sitesSiteStatusData,
    equipmentsEquipmentTypeData,
    equipmentsEquipmentStatusData,
    equipmentsEquipmentSubTypeData,
    equipmentsEquipmentCategoryData,
    sitesAreaHierarchyTreeData,
    sitesWorkOrderPriorityDataMap
  };
};

export const getFilterEquipmentWithPromise = ({ args }) => {
  return new Promise((resolve) => {
    const response = getFilterEquipment({ ...args });
    resolve(response);
  });
};

const dueDateSort = (a, b) => {
  if (!a.soonestDueDate || !b.soonestDueDate) {
    return a.soonestDueDate ? -1 : 1;
  }

  const dateA = new Date(a.soonestDueDate);
  const dateB = new Date(b.soonestDueDate);

  if (dateA < dateB) {
    return -1;
  } else if (dateA > dateB) {
    return 1;
  } else {
    return 0;
  }
};

const distanceFilter = (a, b, sitesMap, cacheSites) => {
  return (
    (cacheSites[sitesMap[a?.siteId]]?.distance != null
      ? cacheSites[sitesMap[a?.siteId]]?.distance
      : Infinity) -
      (cacheSites[sitesMap[b?.siteId]]?.distance != null
        ? cacheSites[sitesMap[b?.siteId]]?.distance
        : Infinity) || a?.name?.localeCompare(b.name)
  );
};

export const tagsDataList = ['DISTANCE', '# TASKS', 'DUE'];

export const mapValues = ({
  sitesSiteTypeDataMap,
  sitesSiteStatusDataMap,
  sitesAssignableUsersDataMap,
  sitesCheckPriorityDataMap,
  sitesWorkOrderPriorityDataMap,
  sitesAreaHierarchyTreeDataMap,
  equipmentsEquipmentTypeDataMap,
  equipmentsEquipmentStatusDataMap,
  equipmentsEquipmentSubTypeDataMap,
  equipmentsEquipmentCategoryDataMap,
  equipments = [],
  store
}) => {
  const { picklists, tasks: reduxTasks, checks, inspection } = store;
  const { workOrderUsersGroupsMap = {}, tasksList = [] } = reduxTasks;
  const {
    siteTypeData = [],
    siteStatusData = [],
    checkPriorityData = [],
    assignableUsersData = [],
    workOrderPriorityData = [],
    areaHierarchyTreeData = [],
    equipmentCategoryData = [],
    equipmentTypeData = [],
    equipmentSubTypeData = [],
    equipmentStatusData = [],
    picklistsMap = {}
  } = picklists;
  const { checkTypesMap } = checks;
  const {
    inspectionTypesMap = {},
    incidentCategoryMap = {},
    inspectionFormsUserCacheTypeMap = {},
    inspectionFormsUserCacheMap = {}
  } = inspection;
  const { workOrderTypeTags, workOrderTypeTagMap } =
    getWorkOrderTypeTags(equipments, picklistsMap) || [];

  let siteTypeTags = sitesSiteTypeDataMap
    ? siteTypeData?.filter(({ key }) => sitesSiteTypeDataMap?.[key])
    : siteTypeData;

  siteTypeTags = sortFilterTags({
    list: siteTypeTags,
    isCheckField: true,
    fieldName: 'value'
  });

  let siteStatusTags = sitesSiteStatusDataMap
    ? siteStatusData?.filter(({ key }) => sitesSiteStatusDataMap[key])
    : siteStatusData;

  siteStatusTags = sortFilterTags({
    list: siteStatusTags,
    isCheckField: true,
    fieldName: 'value'
  });

  const assignedUserGroups = getEquipmentTaskAssignedUserGroups(equipments);
  const assignableUsersTags = getTaskAssignedToTags({
    assignedUserGroups,
    workOrderUsersGroupsMap
  });

  let checkPriorityTags = sitesCheckPriorityDataMap
    ? checkPriorityData.filter(({ key }) => sitesCheckPriorityDataMap[key])
    : checkPriorityData;

  checkPriorityTags = sortFilterTags({
    list: checkPriorityTags,
    isCheckField: true,
    fieldName: 'value'
  });

  let workOrderPriorityTags = sitesWorkOrderPriorityDataMap
    ? workOrderPriorityData.filter(({ key }) => sitesWorkOrderPriorityDataMap[key])
    : workOrderPriorityData;

  workOrderPriorityTags = sortFilterTags({
    list: workOrderPriorityTags,
    isCheckField: true,
    fieldName: 'value'
  });

  const treeData = sitesAreaHierarchyTreeDataMap
    ? removeUnavailableTreeData(
        JSON.parse(JSON.stringify(areaHierarchyTreeData)),
        sitesAreaHierarchyTreeDataMap
      )
    : areaHierarchyTreeData;

  let equipmentCategoryTags = equipmentsEquipmentCategoryDataMap
    ? equipmentCategoryData.filter(({ key }) => equipmentsEquipmentCategoryDataMap[key])
    : equipmentCategoryData;

  let equipmentTypeTags = equipmentsEquipmentTypeDataMap
    ? equipmentTypeData.filter(({ key }) => equipmentsEquipmentTypeDataMap[key])
    : equipmentTypeData;

  let equipmentSubTypeTags = equipmentsEquipmentSubTypeDataMap
    ? equipmentSubTypeData.filter(({ key }) => equipmentsEquipmentSubTypeDataMap[key])
    : equipmentSubTypeData;

  let equipmentStatusTags = equipmentsEquipmentStatusDataMap
    ? equipmentStatusData.filter(({ key }) => equipmentsEquipmentStatusDataMap[key])
    : equipmentStatusData;

  equipmentCategoryTags = sortFilterTags({
    list: equipmentCategoryTags,
    isCheckField: true,
    fieldName: 'value'
  });
  equipmentTypeTags = sortFilterTags({
    list: equipmentTypeTags,
    isCheckField: true,
    fieldName: 'value'
  });
  equipmentSubTypeTags = sortFilterTags({
    list: equipmentSubTypeTags,
    isCheckField: true,
    fieldName: 'value'
  });
  equipmentStatusTags = sortFilterTags({
    list: equipmentStatusTags,
    isCheckField: true,
    fieldName: 'value'
  });

  const taskTypeTag = getEquipmentsTaskTypeTags(equipments);

  const taskStatusTags = getEquipmentsTaskStatusTags(equipments);
  const taskRecurringTemplateTags = getTaskRecurringTemplateTags(equipments) || [];

  const checkRecurrancePriorityTags = getCheckRecurrancePriorityTags(equipments, picklistsMap);
  const { checkTypeTags, checkTypeTagsMap } = getCheckTypeTags(equipments, checkTypesMap);

  const { inspectionTypeTags, inspectionTypeTagsMap } = getInspectionTypeTags(
    equipments,
    inspectionTypesMap,
    inspectionFormsUserCacheTypeMap,
    inspectionFormsUserCacheMap
  );

  const inspectionRunTags = getInspectionRunTags(equipments);

  return {
    inspectionTypeTags,
    inspectionTypeTagsMap,
    inspectionRunTags,
    siteTypeTags,
    siteStatusTags,
    assignableUsersTags,
    checkPriorityTags,
    workOrderPriorityTags,
    treeData,
    equipmentCategoryTags,
    equipmentTypeTags,
    equipmentSubTypeTags,
    equipmentStatusTags,
    taskTypeTag,
    taskStatusTags,
    taskRecurringTemplateTags,
    workOrderTypeTags,
    checkRecurrancePriorityTags,
    checkTypeTags,
    checkTypeTagsMap,
    workOrderTypeTagMap
  };
};

const getCheckTypeTags = (equipments = [], checkTypesMap = {}) => {
  let checkTypeTags = [];

  for (let equipment of equipments) {
    const equipmentTask = equipment?.allTasksForSite || [];
    for (let task of equipmentTask) {
      if (task?.type === 'Check' && checkTypesMap[task?.inspectionTypeId]) {
        if (
          checkTypesMap[task?.inspectionTypeId] &&
          !checkTypeTags.includes(checkTypesMap[task?.inspectionTypeId])
        )
          checkTypeTags.push(checkTypesMap[task?.inspectionTypeId]);
      }
    }
  }

  const checkTypeTagsMap = checkTypeTags?.reduce((acc, curr) => {
    acc[curr.key] = { value: curr.value };
    return acc;
  }, {});

  checkTypeTags = sortFilterTags({
    list: checkTypeTags,
    isCheckField: true,
    fieldName: 'value'
  });

  return { checkTypeTags, checkTypeTagsMap };
};

const getInspectionTypeTags = (
  equipments = [],
  inspectionTypesMap = {},
  inspectionFormsUserCacheTypeMap = {},
  inspectionFormsUserCacheMap
) => {
  const tasks = equipments?.flatMap((x, i) => x.allTasksForSite);
  let inspectionTypeTags = [];

  for (let task of tasks) {
    const inspectionFormId = task?.inspectionFormId || '';
    let inspectionFormName = inspectionFormsUserCacheMap[inspectionFormId] || '';

    const original = inspectionTypesMap[task?.inspectionTypeId];
    if (
      inspectionFormName &&
      !inspectionTypeTags.some((tag) => tag.value === inspectionFormName?.name)
    ) {
      const obj = {
        ...original,
        value: inspectionFormName?.name,
        inspectionFormId: inspectionFormName?.inspectionFormId,
        key: inspectionFormName?.inspectionFormId
      };
      inspectionTypeTags.push(obj);
    }
  }

  const inspectionTypeTagsMap = inspectionTypeTags?.reduce((acc, curr) => {
    acc[curr.inspectionFormId] = { value: curr.value };
    return acc;
  }, {});

  inspectionTypeTags = sortFilterTags({
    list: inspectionTypeTags,
    isCheckField: true,
    fieldName: 'value'
  });

  return { inspectionTypeTags, inspectionTypeTagsMap };
};

const getInspectionRunTags = (equipments = []) => {
  const tasks = equipments.flatMap((x) => x.allTasksForSite);

  const tags = Array.from(
    new Set(
      tasks.map((task) => {
        if (task?.runNumber) return `${task.runNumber} - ${task?.RunName?.trim()}`;
      })
    )
  ).filter((y) => y);
  return sortFilterTags({
    list: tags,
    isCheckField: false,
    fieldName: ''
  });
};

const getCheckRecurrancePriorityTags = (equipments = [], picklistsMap) => {
  let checkRecurrenceTags = [];

  for (let equipment of equipments) {
    const equipmentTask = equipment?.allTasksForSite || [];
    for (let task of equipmentTask) {
      if (
        task?.type === 'Check' &&
        task?.recurrencePriorityId &&
        picklistsMap[task?.recurrencePriorityId]
      ) {
        let recurreanceValue = picklistsMap[task?.recurrencePriorityId] || {};
        let recurreanceValueCopy = { ...recurreanceValue };
        recurreanceValueCopy.value = recurreanceValue?.value?.split(' ')[0] || '';
        if (!checkRecurrenceTags.some((tag) => tag.value === recurreanceValueCopy.value)) {
          checkRecurrenceTags.push(recurreanceValueCopy);
        }
      }
    }
  }

  checkRecurrenceTags = sortFilterTags({
    list: checkRecurrenceTags,
    isCheckField: true,
    fieldName: 'value'
  });

  return checkRecurrenceTags;
};

const getWorkOrderTypeTags = (equipments = [], picklistsMap = {}) => {
  let workOrderTypeTags = [];

  equipments.map((item) =>
    item?.allTasksForSite?.map((task) => {
      if (picklistsMap[task?.typeId] && !workOrderTypeTags.includes(picklistsMap[task?.typeId]))
        workOrderTypeTags.push(picklistsMap[task?.typeId]);
    })
  );

  const workOrderTypeTagMap = workOrderTypeTags?.reduce((acc, curr) => {
    acc[curr.key] = { value: curr.value };
    return acc;
  }, {});

  return { workOrderTypeTags, workOrderTypeTagMap };
};

const getTaskRecurringTemplateTags = (equipments = []) => {
  let tags = Array.from(
    new Set(
      equipments
        .map(
          (item) =>
            item.allTasksForSite &&
            item.allTasksForSite.map((task) => {
              if (task.rtNumber) return `${task.rtNumber} - ${task.rtName}`;
            })
        )
        .flat()
        .filter((name) => name)
    )
  );
  tags = sortFilterTags({
    list: tags,
    isCheckField: false,
    fieldName: ''
  });

  return tags;
};

const getEquipmentsTaskTypeTags = (equipments) => {
  let taskTypeTag = [];
  for (let equipment of equipments) {
    if (
      _.size(equipment.allTasksForSite) > 0 &&
      equipment.allTasksForSite.some((x) => x.type === 'Work Order')
    ) {
      taskTypeTag.push('Work orders');
      break;
    }
  }
  for (let equipment of equipments) {
    if (
      _.size(equipment.allTasksForSite) > 0 &&
      equipment.allTasksForSite.some((x) => x.type === 'Work Request' || x.newWorkRequest)
    ) {
      taskTypeTag.push('Work requests');
      break;
    }
  }
  for (let equipment of equipments) {
    if (
      _.size(equipment.allTasksForSite) > 0 &&
      equipment.allTasksForSite.some((x) => x.type === 'Inspection')
    ) {
      taskTypeTag.push('Inspections');
      break;
    }
  }
  for (let equipment of equipments) {
    if (
      _.size(equipment.allTasksForSite) > 0 &&
      equipment.allTasksForSite.some((x) => x.type === 'Check')
    ) {
      taskTypeTag.push('Check');
      break;
    }
  }

  if (!taskTypeTag.includes('New or edited asset')) {
    for (let equipment of equipments) {
      if (_.size(equipment?.equipmentSyncedAndEditedTasks) > 0) {
        taskTypeTag.push('New or edited asset');
        break;
      }
    }
  }

  return taskTypeTag?.length > 0 ? [...taskTypeTag, 'All'] : taskTypeTag;
};

const getEquipmentsTaskStatusTags = (equipments) => {
  let taskStatusTags = [];
  for (let equipment of equipments) {
    if (
      _.size(equipment.allTasksForSite) > 0 &&
      equipment.allTasksForSite.some((x) => x.status === 'To do')
    ) {
      taskStatusTags.push('To do');
      break;
    }
  }
  for (let equipment of equipments) {
    if (
      (_.size(equipment.allTasksForSite) > 0 &&
        equipment.allTasksForSite.some((x) => x.status === 'In progress')) ||
      (_.size(equipment.equipmentSyncedAndEditedTasks) > 0 &&
        equipment.equipmentSyncedAndEditedTasks.some((x) => x.status === 'In progress'))
    ) {
      taskStatusTags.push('In progress');
      break;
    }
  }
  for (let equipment of equipments) {
    if (
      (_.size(equipment.allTasksForSite) > 0 &&
        equipment.allTasksForSite.some((x) => x.status === 'Pending sync')) ||
      (_.size(equipment.equipmentSyncedAndEditedTasks) > 0 &&
        equipment.equipmentSyncedAndEditedTasks.some((x) => x.status === 'Pending sync'))
    ) {
      taskStatusTags.push('Pending sync');
      break;
    }
  }
  for (let equipment of equipments) {
    if (
      (_.size(equipment.allTasksForSite) > 0 &&
        equipment.allTasksForSite.some((x) => x.status === 'Synced')) ||
      (_.size(equipment.equipmentSyncedAndEditedTasks) > 0 &&
        equipment.equipmentSyncedAndEditedTasks.some((x) => x.status === 'Synced'))
    ) {
      taskStatusTags.push('Synced');
      break;
    }
  }
  return taskStatusTags;
};

const getEquipmentTaskAssignedUserGroups = (equipments) => {
  let assignedUserGroups = [];
  for (let equipment of equipments) {
    if (_.size(equipment.allTasksForSite) > 0) {
      assignedUserGroups.push(
        Array.from(
          new Set(equipment.allTasksForSite.map((t) => t?.responsibleUserGroups).flat(2))
        ) || []
      );
    }
  }
  return Array.from(new Set(_.flattenDeep(assignedUserGroups))) || [];
};

const getTaskAssignedToTags = ({ assignedUserGroups, workOrderUsersGroupsMap }) => {
  let data =
    (assignedUserGroups &&
      _.size(assignedUserGroups) > 0 &&
      assignedUserGroups
        .map((key) => workOrderUsersGroupsMap[key] && workOrderUsersGroupsMap[key]?.name)
        .filter((y) => y)) ||
    [];

  let assignableUsersTags = ['Me, directly', 'A group I’m in', ...data];

  return assignableUsersTags;
};

export const countFilters = (filters, inputValue, store) => {
  let count = 0;
  const { picklists } = store;

  const {
    proximity,
    area,
    siteType,
    siteStatus,
    taskType,
    taskStatus,
    assign,
    priority,
    workOrderPriority,
    equipmentCategory,
    equipmentType,
    equipmentSubType,
    equipmentStatus,
    workOrderType = { value: [] },
    recurringTemplate = { value: [] },
    checkRecurrancePriority = { value: [] },
    inspectionType = { value: [] },
    inspectionRun = { value: [] },
    checkType = { value: [] }
  } = filters;

  if (inputValue?.trim()) ++count;
  if (recurringTemplate?.value?.length > 0) ++count;
  if (proximity.value > 0) ++count;
  if (workOrderType?.value?.length > 0) ++count;
  if (assign?.value?.length > 0) ++count;
  if (siteType?.value?.length > 0) ++count;
  if (taskType?.value?.length > 0) ++count;
  if (taskStatus?.value?.length > 0) ++count;
  if (siteStatus?.value?.length > 0) ++count;
  if (equipmentType?.value?.length > 0) ++count;
  if (equipmentStatus?.value?.length > 0) ++count;
  if (equipmentSubType?.value?.length > 0) ++count;
  if (equipmentCategory?.value?.length > 0) ++count;
  if (checkRecurrancePriority?.value.length > 0) ++count;
  if (inspectionType?.value?.length > 0) ++count;
  if (inspectionRun?.value?.length > 0) ++count;
  if (checkType?.value?.length > 0) ++count;

  if (priority?.value?.length > 0 || workOrderPriority?.value?.length > 0) ++count;
  if (
    area?.value &&
    area?.value.filter((el) => picklists.areaHierarchyTreeDataMap?.[el])?.length > 0
  )
    ++count;

  ['dueDate', 'scheduledDate', 'createdAt', 'syncedDate'].forEach((key) => {
    const { value, selected } = filters[key] || { value: '', selected: '' };
    if (
      selected &&
      !_.isEmpty(value) &&
      (selected === 'on'
        ? value !== ''
        : selected === 'range'
        ? value?.[0] !== '' && value?.[0] !== ''
        : value?.length > 0)
    )
      ++count;
  });
  return count;
};

export const getEquipmentDefaultFilters = {
  equipmentCategory: { value: [] },
  equipmentType: { value: [] },
  equipmentSubType: { value: [] },
  equipmentStatus: { value: [] },
  proximity: { value: 0 },
  area: { value: [] },
  siteType: { value: [] },
  siteStatus: { value: [] },
  taskType: { value: [] },
  taskStatus: { value: [] },
  assign: { value: [] },
  priority: { value: [] },
  recurringTemplate: { value: [] },
  workOrderType: { value: [] },
  workOrderPriority: { value: [] },
  download: { value: '', selected: 'past' },
  createdAt: { value: '', selected: 'past' },
  syncedDate: { value: '', selected: 'past' },
  dueDate: { value: [], selected: 'next' },
  scheduledDate: { value: [], selected: 'next' },
  checkRecurrancePriority: { value: [] },
  inspectionType: { value: [] },
  checkType: { value: [] }
};
